/**
 * Aviso de Privacidade + registro de aceite (LGPD).
 * Mostra o texto e registra quem deu ciência e quando (por versão do aviso).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, FileText, Loader2 } from "lucide-react";
import { useState } from "react";
import { api } from "../lib/api";

interface Aviso {
  versao: string;
  texto: string;
  aceite: { aceito: boolean; por: string | null; aceito_em: string | null };
}

function fmtData(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function PrivacidadeCard() {
  const qc = useQueryClient();
  const { data } = useQuery<Aviso>({
    queryKey: ["lgpd", "privacidade"],
    queryFn: () => api.get<Aviso>("/lgpd/privacidade"),
  });
  const [nome, setNome] = useState("");
  const aceitar = useMutation({
    mutationFn: () => api.post("/lgpd/privacidade/aceite", { nome }),
    onSuccess: () => {
      setNome("");
      qc.invalidateQueries({ queryKey: ["lgpd", "privacidade"] });
    },
  });

  const aceito = !!data?.aceite.aceito;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
      <div className="flex items-center gap-2 mb-1">
        <FileText size={17} className="text-violet-600" />
        <h4 className="text-sm font-semibold text-slate-900">Aviso de Privacidade</h4>
        <span
          className={`ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
            aceito
              ? "text-emerald-700 bg-emerald-50 border-emerald-200"
              : "text-amber-700 bg-amber-50 border-amber-200"
          }`}
        >
          {aceito ? "Ciente registrado" : "Sem ciência"}
        </span>
      </div>
      <p className="text-[11px] text-slate-400 mb-2">
        Versão {data?.versao ?? "—"} · rascunho (a redação final é jurídica).
      </p>

      <pre className="text-[12px] text-slate-700 whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-64 overflow-auto font-sans leading-relaxed">
        {data?.texto ?? "Carregando…"}
      </pre>

      {aceito ? (
        <p className="mt-3 text-[13px] text-emerald-700 inline-flex items-center gap-1.5">
          <CheckCircle2 size={15} />
          Ciência registrada{data?.aceite.por ? ` por ${data.aceite.por}` : ""} em{" "}
          {fmtData(data?.aceite.aceito_em ?? null)}.
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Seu nome (opcional)"
            className="px-2 py-1.5 border border-slate-300 rounded-md text-sm"
          />
          <button
            type="button"
            onClick={() => aceitar.mutate()}
            disabled={aceitar.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white text-sm font-medium rounded-md"
          >
            {aceitar.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <CheckCircle2 size={14} />
            )}
            Li e estou ciente
          </button>
        </div>
      )}
    </div>
  );
}
